import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CheckCircle, Clock, AlertTriangle, XCircle, RefreshCw, History, ListChecks, ShieldCheck, ShieldAlert, ChevronLeft, ChevronRight, Building2, Briefcase, ShieldQuestion, UserX, Tag, FileText, Paperclip } from 'lucide-react';
import { abrirArquivoComToken } from '../../../utils/arquivoComToken';

const API_BASE = '/api/rh/controle-experiencia';
const ITENS_POR_PAGINA = 50;

const formatarData = (data) => {
    if (!data) return '-';
    const d = new Date(data);
    const utc = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
    return utc.toLocaleDateString('pt-BR');
};

const formatarCPF = (cpf) => {
    if (!cpf) return '-';
    const c = cpf.replace(/\D/g, '');
    if (c.length !== 11) return cpf;
    return c.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const calcularDiasRestantes = (dataFim) => {
    if (!dataFim) return null;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const fim = new Date(dataFim);
    const utc = new Date(fim.getTime() + fim.getTimezoneOffset() * 60000);
    utc.setHours(0, 0, 0, 0);
    return Math.ceil((utc - hoje) / (1000 * 60 * 60 * 24));
};

const getStatusCor = (diasRestantes) => {
    if (diasRestantes === null) return { color: '#64748b', bg: '#f1f5f9', label: '-', icon: null };
    if (diasRestantes <= 0)  return { color: '#dc2626', bg: '#fee2e2', label: 'Vencido',           icon: XCircle };
    if (diasRestantes <= 15) return { color: '#d97706', bg: '#fef3c7', label: `${diasRestantes}d`, icon: AlertTriangle };
    return                          { color: '#059669', bg: '#d1fae5', label: `${diasRestantes}d`, icon: Clock };
};

// Badge da situação da baixa no Histórico:
//  - Retirado: termo de experiência ajustado no Populis (saída "positiva")
//  - Desligado: contrato encerrado antes do fim da experiência (baixa automática — a pessoa saiu)
const BadgeSituacao = ({ status }) => {
    const desligado = status === 'Desligado';
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '4px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '700',
            whiteSpace: 'nowrap',
            color:           desligado ? '#7c2d12' : '#065f46',
            backgroundColor: desligado ? '#ffedd5' : '#d1fae5',
            border:          desligado ? '1px solid #fdba74' : '1px solid #6ee7b7',
        }}>
            {desligado ? <><UserX size={11} /> Desligado</> : <><CheckCircle size={11} /> Retirado</>}
        </span>
    );
};

// Badge de status do Populis (verificação em tempo real via dtbConfere)
const BadgePopulis = ({ valor }) => {
    const ok = valor === 1;
    return (
        <span
            title={ok
                ? 'Populis atualizado — DATA_FINAL_EXPERIENCIA1 e DATA_FINAL_EXPERIENCIA2 = DATA_ADMISSAO'
                : 'Aguardando sync do Populis (04:30 · 12:30 · 16:20)'}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '4px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: '700',
                color:           ok ? '#065f46' : '#92400e',
                backgroundColor: ok ? '#d1fae5' : '#fef3c7',
                border:          ok ? '1px solid #6ee7b7' : '1px solid #fde68a',
                whiteSpace: 'nowrap',
            }}
        >
            {ok ? <><ShieldCheck size={11} /> Populis OK</> : <><ShieldAlert size={11} /> Aguardando</>}
        </span>
    );
};

const RHControleExperiencia = () => {
    const token = localStorage.getItem('authToken');
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

    const [abaAtiva, setAbaAtiva]       = useState('pendentes');
    const [pendentes, setPendentes]     = useState([]);
    const [historico, setHistorico]     = useState([]);
    const [loading, setLoading]         = useState(false);
    const [erro, setErro]               = useState(null);
    const [modal, setModal]             = useState({ aberto: false, item: null, observacao: '' });
    // ⚠️ O PDF de término fica AQUI, no estado do pai, e não no <input>: o Modal é
    //    remontado a cada render (ex.: ao digitar a observação), o que zeraria um
    //    input não controlado. Guardando o File no pai, a escolha sobrevive.
    const [docTermino, setDocTermino]   = useState(null);
    const [salvando, setSalvando]       = useState(false);
    const [resultado, setResultado]     = useState(null);
    const [filtroEmpresa, setFiltroEmpresa] = useState('');
    const [pagina, setPagina]           = useState(1);

    // Filtros e paginação independentes da aba Histórico
    const [filtroEmpresaHist, setFiltroEmpresaHist] = useState('');
    const [filtroCargoHist, setFiltroCargoHist]     = useState('');
    const [filtroStatusHist, setFiltroStatusHist]   = useState(''); // '', 'confirmado', 'naoconfirmado', 'indefinido'
    const [filtroSituacaoHist, setFiltroSituacaoHist] = useState(''); // '', 'Retirado', 'Desligado'
    const [paginaHist, setPaginaHist]               = useState(1);

    // Lista de empresas únicas para o filtro
    const empresas = useMemo(
        () => [...new Set(pendentes.map(r => r.NOME_EMPRESA).filter(Boolean))].sort(),
        [pendentes]
    );

    // Pendentes filtrados por empresa
    const pendentesFiltrados = useMemo(
        () => filtroEmpresa ? pendentes.filter(r => r.NOME_EMPRESA === filtroEmpresa) : pendentes,
        [pendentes, filtroEmpresa]
    );

    const totalPaginas   = Math.max(1, Math.ceil(pendentesFiltrados.length / ITENS_POR_PAGINA));
    const pendentesPagina = pendentesFiltrados.slice((pagina - 1) * ITENS_POR_PAGINA, pagina * ITENS_POR_PAGINA);

    // Volta pra página 1 quando o filtro muda
    useEffect(() => { setPagina(1); }, [filtroEmpresa]);

    // ─── Histórico: listas para os filtros ───────────────────────────────────
    const empresasHist = useMemo(
        () => [...new Set(historico.map(r => r.NOME_EMPRESA).filter(Boolean))].sort(),
        [historico]
    );
    const cargosHist = useMemo(
        () => [...new Set(historico.map(r => r.NOME_CARGO_ABREV).filter(Boolean))].sort(),
        [historico]
    );

    // Classifica o status Populis de uma linha em uma das chaves do filtro
    const statusPopulisKey = (row) => {
        if (row.AJUSTE_POPULIS_CONFIRMADO === 1) return 'confirmado';
        if (row.AJUSTE_POPULIS_CONFIRMADO === 0) return 'naoconfirmado';
        return 'indefinido';
    };

    // Histórico filtrado por situação (Retirado/Desligado) + empresa + cargo + status Populis
    const historicoFiltrado = useMemo(
        () => historico.filter(r => {
            if (filtroSituacaoHist && r.STATUS !== filtroSituacaoHist) return false;
            if (filtroEmpresaHist && r.NOME_EMPRESA !== filtroEmpresaHist) return false;
            if (filtroCargoHist && r.NOME_CARGO_ABREV !== filtroCargoHist) return false;
            if (filtroStatusHist && statusPopulisKey(r) !== filtroStatusHist) return false;
            return true;
        }),
        [historico, filtroSituacaoHist, filtroEmpresaHist, filtroCargoHist, filtroStatusHist]
    );

    const totalPaginasHist = Math.max(1, Math.ceil(historicoFiltrado.length / ITENS_POR_PAGINA));
    const historicoPagina  = historicoFiltrado.slice((paginaHist - 1) * ITENS_POR_PAGINA, paginaHist * ITENS_POR_PAGINA);

    // Volta pra página 1 quando qualquer filtro do histórico muda
    useEffect(() => { setPaginaHist(1); }, [filtroSituacaoHist, filtroEmpresaHist, filtroCargoHist, filtroStatusHist]);

    const buscarPendentes = useCallback(async () => {
        setLoading(true);
        setErro(null);
        try {
            const res = await fetch(API_BASE, { headers });
            if (!res.ok) throw new Error('Erro ao buscar dados.');
            setPendentes(await res.json());
            setPagina(1);
        } catch (e) {
            setErro(e.message);
        } finally {
            setLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const buscarHistorico = useCallback(async () => {
        setLoading(true);
        setErro(null);
        try {
            const res = await fetch(`${API_BASE}/historico`, { headers });
            if (!res.ok) throw new Error('Erro ao buscar histórico.');
            setHistorico(await res.json());
        } catch (e) {
            setErro(e.message);
        } finally {
            setLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (abaAtiva === 'pendentes') buscarPendentes();
        else buscarHistorico();
    }, [abaAtiva]); // eslint-disable-line react-hooks/exhaustive-deps

    const confirmarRetirada = async () => {
        if (!modal.item) return;
        // PDF obrigatório: é a documentação de término que a baixa manual registra.
        if (!docTermino) {
            setErro('Anexe o documento de término do contrato de experiência (PDF).');
            return;
        }
        setSalvando(true);
        setResultado(null);
        try {
            // ⚠️ multipart (FormData): mando o PDF + a observação. NÃO seto
            //    Content-Type — o browser monta o boundary sozinho; declará-lo
            //    quebra o upload. Por isso só o Authorization vai no header.
            const corpo = new FormData();
            corpo.append('documento', docTermino);
            corpo.append('observacao', modal.observacao || '');
            const res = await fetch(`${API_BASE}/${modal.item.ID}/retirar`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: corpo,
            });
            if (!res.ok) {
                const d = await res.json().catch(() => ({}));
                throw new Error(d.message || 'Erro ao atualizar registro.');
            }
            const data = await res.json();
            setResultado({ ajustePopulisConfirmado: data.ajustePopulisConfirmado });
            setModal({ aberto: false, item: null, observacao: '' });
            setDocTermino(null);
            buscarPendentes();
        } catch (e) {
            setErro(e.message);
        } finally {
            setSalvando(false);
        }
    };

    // ─── ESTILOS COMPARTILHADOS ──────────────────────────────────────────────
    const thStyle = {
        padding: '12px 16px', textAlign: 'left', fontSize: '11px',
        fontWeight: '700', textTransform: 'uppercase', color: '#8b6464',
        borderBottom: '2px solid #f0e2e2', whiteSpace: 'nowrap',
    };
    const tdStyle = {
        padding: '14px 16px', fontSize: '14px', color: '#1e293b',
        borderBottom: '1px solid #f9f1f1', verticalAlign: 'middle',
    };

    // ─── CONTROLES DE PAGINAÇÃO ──────────────────────────────────────────────
    const Paginacao = ({ total, pagina, setPagina, totalPaginas }) => {
        if (totalPaginas <= 1) return null;
        const btnBase = {
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '7px 14px', borderRadius: '8px', border: '1px solid #f0e2e2',
            fontSize: '13px', fontWeight: '600', cursor: 'pointer',
            backgroundColor: '#fff', color: '#475569', transition: 'background 0.15s',
        };
        const btnDisabled = { ...btnBase, opacity: 0.4, cursor: 'not-allowed' };
        return (
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '14px 16px', borderTop: '1px solid #f9f1f1',
            }}>
                <span style={{ fontSize: '13px', color: '#8b6464' }}>
                    {total} registro{total !== 1 ? 's' : ''}
                    {totalPaginas > 1 && <> · página <strong>{pagina}</strong> de <strong>{totalPaginas}</strong></>}
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={() => setPagina(p => Math.max(1, p - 1))}
                        disabled={pagina === 1}
                        style={pagina === 1 ? btnDisabled : btnBase}
                    >
                        <ChevronLeft size={15} /> Anterior
                    </button>
                    <button
                        onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
                        disabled={pagina >= totalPaginas}
                        style={pagina >= totalPaginas ? btnDisabled : btnBase}
                    >
                        Próxima <ChevronRight size={15} />
                    </button>
                </div>
            </div>
        );
    };

    // ─── TABELA DE PENDENTES ─────────────────────────────────────────────────
    const TabelaPendentes = () => (
        <div>
            {/* Filtro por empresa */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                marginBottom: '16px', flexWrap: 'wrap',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#8b6464', fontSize: '13px', fontWeight: '600' }}>
                    <Building2 size={15} /> Empresa
                </div>
                <select
                    value={filtroEmpresa}
                    onChange={e => setFiltroEmpresa(e.target.value)}
                    style={{
                        padding: '7px 12px', borderRadius: '8px', border: '1px solid #f0e2e2',
                        fontSize: '13px', color: '#1e293b', backgroundColor: '#fff',
                        cursor: 'pointer', minWidth: '200px', maxWidth: '320px',
                    }}
                >
                    <option value=''>Todas ({pendentes.length})</option>
                    {empresas.map(emp => {
                        const qtd = pendentes.filter(r => r.NOME_EMPRESA === emp).length;
                        return (
                            <option key={emp} value={emp}>{emp} ({qtd})</option>
                        );
                    })}
                </select>
                {filtroEmpresa && (
                    <button
                        onClick={() => setFiltroEmpresa('')}
                        style={{
                            padding: '6px 12px', borderRadius: '8px', border: '1px solid #f0e2e2',
                            fontSize: '12px', fontWeight: '600', color: '#8b6464',
                            backgroundColor: '#fff', cursor: 'pointer',
                        }}
                    >
                        ✕ Limpar filtro
                    </button>
                )}
            </div>

            {pendentesFiltrados.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#8b6464' }}>
                    <CheckCircle size={48} color="#10b981" style={{ marginBottom: '16px' }} />
                    <p style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                        {filtroEmpresa ? `Nenhum pendente para "${filtroEmpresa}".` : 'Nenhum funcionário pendente no momento.'}
                    </p>
                </div>
            ) : (
                <div style={{ border: '1px solid #f0e2e2', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#fcf8f8' }}>
                                    <th style={thStyle}>Nome</th>
                                    <th style={thStyle}>CPF</th>
                                    <th style={thStyle}>Empresa</th>
                                    <th style={thStyle}>Cargo</th>
                                    <th style={thStyle}>Admissão</th>
                                    <th style={thStyle}>Fim Experiência</th>
                                    <th style={{ ...thStyle, textAlign: 'center' }}>Prazo</th>
                                    <th style={{ ...thStyle, textAlign: 'center' }}>Populis</th>
                                    <th style={{ ...thStyle, textAlign: 'center' }}>Ação</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pendentesPagina.map((row) => {
                                    const dias = calcularDiasRestantes(row.DATA_FIM_EXPERIENCIA);
                                    const cor  = getStatusCor(dias);
                                    const IconePrazo = cor.icon;
                                    return (
                                        <tr key={row.ID} style={{ transition: 'background 0.15s' }}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fcf8f8'}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                            <td style={{ ...tdStyle, fontWeight: '600' }}>{row.NOME_PESSOA || '-'}</td>
                                            <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '13px' }}>{formatarCPF(row.CPF)}</td>
                                            <td style={tdStyle}>{row.NOME_EMPRESA || '-'}</td>
                                            <td style={tdStyle}>{row.NOME_CARGO_ABREV || '-'}</td>
                                            <td style={tdStyle}>{formatarData(row.DATA_ADMISSAO_ATUAL)}</td>
                                            <td style={tdStyle}>{formatarData(row.DATA_FIM_EXPERIENCIA)}</td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <span style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                                                    padding: '4px 10px', borderRadius: '999px',
                                                    fontSize: '12px', fontWeight: '700',
                                                    color: cor.color, backgroundColor: cor.bg,
                                                }}>
                                                    {IconePrazo && <IconePrazo size={13} />}
                                                    {cor.label}
                                                </span>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <BadgePopulis valor={row.AJUSTE_POPULIS} />
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <button
                                                    onClick={() => setModal({ aberto: true, item: row, observacao: '' })}
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                        padding: '7px 14px', borderRadius: '8px', border: 'none',
                                                        backgroundColor: '#d81d1d', color: '#fff',
                                                        fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                                                        transition: 'background 0.2s',
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#af1e1e'}
                                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#d81d1d'}
                                                >
                                                    <CheckCircle size={14} /> Retirado
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Paginacao
                        total={pendentesFiltrados.length}
                        pagina={pagina}
                        setPagina={setPagina}
                        totalPaginas={totalPaginas}
                    />
                </div>
            )}
        </div>
    );

    // ─── TABELA DE HISTÓRICO ─────────────────────────────────────────────────
    const selectStyle = {
        padding: '7px 12px', borderRadius: '8px', border: '1px solid #f0e2e2',
        fontSize: '13px', color: '#1e293b', backgroundColor: '#fff',
        cursor: 'pointer', minWidth: '180px', maxWidth: '320px',
    };
    const labelStyle = { display: 'flex', alignItems: 'center', gap: '6px', color: '#8b6464', fontSize: '13px', fontWeight: '600' };
    const STATUS_OPCOES = [
        { key: 'confirmado',    label: 'Confirmado' },
        { key: 'naoconfirmado', label: 'Não confirmado' },
        { key: 'indefinido',    label: 'Indefinido' },
    ];
    const filtroHistAtivo = filtroSituacaoHist || filtroEmpresaHist || filtroCargoHist || filtroStatusHist;

    const TabelaHistorico = () => (
        <div>
            {/* Barra de filtros: Situação · Empresa · Cargo · Status Populis */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={labelStyle}><Tag size={15} /> Situação</span>
                    <select value={filtroSituacaoHist} onChange={e => setFiltroSituacaoHist(e.target.value)} style={{ ...selectStyle, minWidth: '150px' }}>
                        <option value=''>Todas ({historico.length})</option>
                        <option value='Retirado'>Retirado ({historico.filter(r => r.STATUS === 'Retirado').length})</option>
                        <option value='Desligado'>Desligado ({historico.filter(r => r.STATUS === 'Desligado').length})</option>
                    </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={labelStyle}><Building2 size={15} /> Empresa</span>
                    <select value={filtroEmpresaHist} onChange={e => setFiltroEmpresaHist(e.target.value)} style={selectStyle}>
                        <option value=''>Todas ({historico.length})</option>
                        {empresasHist.map(emp => (
                            <option key={emp} value={emp}>
                                {emp} ({historico.filter(r => r.NOME_EMPRESA === emp).length})
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={labelStyle}><Briefcase size={15} /> Cargo</span>
                    <select value={filtroCargoHist} onChange={e => setFiltroCargoHist(e.target.value)} style={selectStyle}>
                        <option value=''>Todos</option>
                        {cargosHist.map(cargo => (
                            <option key={cargo} value={cargo}>
                                {cargo} ({historico.filter(r => r.NOME_CARGO_ABREV === cargo).length})
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={labelStyle}><ShieldQuestion size={15} /> Status Populis</span>
                    <select value={filtroStatusHist} onChange={e => setFiltroStatusHist(e.target.value)} style={{ ...selectStyle, minWidth: '160px' }}>
                        <option value=''>Todos</option>
                        {STATUS_OPCOES.map(opt => (
                            <option key={opt.key} value={opt.key}>
                                {opt.label} ({historico.filter(r => statusPopulisKey(r) === opt.key).length})
                            </option>
                        ))}
                    </select>
                </div>

                {filtroHistAtivo && (
                    <button
                        onClick={() => { setFiltroSituacaoHist(''); setFiltroEmpresaHist(''); setFiltroCargoHist(''); setFiltroStatusHist(''); }}
                        style={{
                            padding: '6px 12px', borderRadius: '8px', border: '1px solid #f0e2e2',
                            fontSize: '12px', fontWeight: '600', color: '#8b6464',
                            backgroundColor: '#fff', cursor: 'pointer',
                        }}
                    >
                        ✕ Limpar filtros
                    </button>
                )}
            </div>

            {historicoFiltrado.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#8b6464' }}>
                    <History size={48} color="#b89494" style={{ marginBottom: '16px' }} />
                    <p style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                        {historico.length === 0 ? 'Nenhum registro no histórico ainda.' : 'Nenhum registro para os filtros selecionados.'}
                    </p>
                </div>
            ) : (
                <div style={{ border: '1px solid #f0e2e2', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#fcf8f8' }}>
                                    <th style={thStyle}>Nome</th>
                                    <th style={thStyle}>CPF</th>
                                    <th style={thStyle}>Empresa</th>
                                    <th style={thStyle}>Cargo</th>
                                    <th style={thStyle}>Admissão</th>
                                    <th style={thStyle}>Fim Experiência</th>
                                    <th style={{ ...thStyle, textAlign: 'center' }}>Situação</th>
                                    <th style={thStyle}>Baixado em</th>
                                    <th style={thStyle}>Responsável</th>
                                    <th style={{ ...thStyle, textAlign: 'center' }}>Populis</th>
                                    <th style={thStyle}>Observação</th>
                                    <th style={{ ...thStyle, textAlign: 'center' }}>Documento</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historicoPagina.map((row) => (
                                    <tr key={row.ID}
                                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fcf8f8'}
                                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                        <td style={{ ...tdStyle, fontWeight: '600' }}>{row.NOME_PESSOA || '-'}</td>
                                        <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '13px' }}>{formatarCPF(row.CPF)}</td>
                                        <td style={tdStyle}>{row.NOME_EMPRESA || '-'}</td>
                                        <td style={tdStyle}>{row.NOME_CARGO_ABREV || '-'}</td>
                                        <td style={tdStyle}>{formatarData(row.DATA_ADMISSAO_ATUAL)}</td>
                                        <td style={tdStyle}>{formatarData(row.DATA_FIM_EXPERIENCIA)}</td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}><BadgeSituacao status={row.STATUS} /></td>
                                        <td style={tdStyle}>{row.DATA_CHECK ? new Date(row.DATA_CHECK).toLocaleString('pt-BR') : '-'}</td>
                                        <td style={tdStyle}>{row.USUARIO_CHECK || '-'}</td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            {row.AJUSTE_POPULIS_CONFIRMADO === 1 && (
                                                <span style={{ color: '#065f46', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    <ShieldCheck size={14} /> Confirmado
                                                </span>
                                            )}
                                            {row.AJUSTE_POPULIS_CONFIRMADO === 0 && (
                                                <span style={{ color: '#92400e', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    <ShieldAlert size={14} /> Não confirmado
                                                </span>
                                            )}
                                            {row.AJUSTE_POPULIS_CONFIRMADO == null && (
                                                <span style={{ color: '#94a3b8' }}>–</span>
                                            )}
                                        </td>
                                        <td style={{ ...tdStyle, color: '#64748b', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {row.OBSERVACAO || '-'}
                                        </td>
                                        {/* Só a baixa MANUAL tem PDF; as automáticas (Populis/Desligado) não. */}
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            {row.DOCUMENTO_URL ? (
                                                <button
                                                    type="button"
                                                    onClick={() => abrirArquivoComToken(`${API_BASE}/${row.ID}/arquivo`, row.DOCUMENTO_NOME || `termino_${row.ID}.pdf`)}
                                                    title="Abrir o documento de término"
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                        color: '#d81d1d', fontWeight: '700', fontSize: '13px',
                                                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                                    }}
                                                >
                                                    <FileText size={14} /> PDF
                                                </button>
                                            ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Paginacao
                        total={historicoFiltrado.length}
                        pagina={paginaHist}
                        setPagina={setPaginaHist}
                        totalPaginas={totalPaginasHist}
                    />
                </div>
            )}
        </div>
    );

    // ─── MODAL DE CONFIRMAÇÃO ────────────────────────────────────────────────
    const Modal = () => {
        if (!modal.aberto || !modal.item) return null;
        const populisOk = modal.item.AJUSTE_POPULIS === 1;
        return (
            <div style={{
                position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
            }}>
                <div style={{
                    backgroundColor: '#fff', borderRadius: '16px', padding: '32px',
                    maxWidth: '480px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                        <div style={{ padding: '10px', borderRadius: '12px', backgroundColor: '#fedbdb' }}>
                            <CheckCircle size={24} color="#d81d1d" />
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#2a0f0f' }}>Confirmar Retirada</h3>
                            <p style={{ margin: 0, fontSize: '13px', color: '#8b6464' }}>Termo de contrato de experiência</p>
                        </div>
                    </div>

                    <div style={{ backgroundColor: '#f8fafc', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                        <p style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: '#3b1e1e' }}>{modal.item.NOME_PESSOA}</p>
                        <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#694747' }}>{modal.item.NOME_EMPRESA} · {modal.item.NOME_CARGO_ABREV}</p>
                        <p style={{ margin: 0, fontSize: '13px', color: '#8b6464' }}>
                            Admissão: {formatarData(modal.item.DATA_ADMISSAO_ATUAL)} · Fim experiência: {formatarData(modal.item.DATA_FIM_EXPERIENCIA)}
                        </p>
                    </div>

                    {/* Status do Populis no modal */}
                    <div style={{
                        display: 'flex', alignItems: 'flex-start', gap: '10px',
                        padding: '12px 14px', borderRadius: '8px', marginBottom: '16px',
                        backgroundColor: populisOk ? '#d1fae5' : '#fef9c3',
                        border: `1px solid ${populisOk ? '#6ee7b7' : '#fde047'}`,
                        fontSize: '13px',
                        color: populisOk ? '#065f46' : '#713f12',
                    }}>
                        {populisOk
                            ? <><ShieldCheck size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
                                <span><strong>Populis confirmado.</strong> As datas de experiência já foram ajustadas.</span></>
                            : <><ShieldAlert size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
                                <span><strong>Populis ainda não atualizado.</strong> Você pode registrar mesmo assim — o status será salvo automaticamente. Ciclos: 04:30 · 12:30 · 16:20.</span></>
                        }
                    </div>

                    {/* DOCUMENTO DE TÉRMINO — obrigatório (pedido da Julia, 21/09).
                        Input de arquivo simples (só PDF): não é foto, é o termo
                        assinado. O File fica no estado do pai (docTermino). */}
                    <p style={{ fontSize: '14px', color: '#694747', marginBottom: '8px', fontWeight: '600' }}>
                        Documento de término <span style={{ color: '#d81d1d' }}>*</span> <span style={{ fontWeight: '400', color: '#b89494' }}>(PDF)</span>
                    </p>
                    <label style={{
                        display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px',
                        padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                        border: `1px solid ${docTermino ? '#6ee7b7' : '#f0e2e2'}`,
                        backgroundColor: docTermino ? '#f0fdf4' : '#fff',
                        color: docTermino ? '#065f46' : '#694747', fontSize: '14px', fontWeight: '600',
                    }}>
                        {docTermino ? <CheckCircle size={16} /> : <Paperclip size={16} />}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {docTermino ? docTermino.name : 'Anexar PDF do término'}
                        </span>
                        <input
                            type="file"
                            accept="application/pdf"
                            onChange={e => setDocTermino(e.target.files[0] || null)}
                            style={{ display: 'none' }}
                        />
                    </label>

                    <p style={{ fontSize: '14px', color: '#694747', marginBottom: '8px', fontWeight: '600' }}>
                        Observação <span style={{ fontWeight: '400', color: '#b89494' }}>(opcional)</span>
                    </p>
                    <textarea
                        value={modal.observacao}
                        onChange={e => setModal(m => ({ ...m, observacao: e.target.value }))}
                        placeholder="Ex: Atualizado no Populis em 26/05/2026"
                        rows={3}
                        style={{
                            width: '100%', padding: '10px 12px', borderRadius: '8px',
                            border: '1px solid #f0e2e2', fontSize: '14px', resize: 'vertical',
                            fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: '20px',
                        }}
                    />

                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <button
                            onClick={() => { setModal({ aberto: false, item: null, observacao: '' }); setDocTermino(null); }}
                            disabled={salvando}
                            style={{
                                padding: '10px 20px', borderRadius: '8px', border: '1px solid #f0e2e2',
                                backgroundColor: '#fff', color: '#475569', fontSize: '14px',
                                fontWeight: '600', cursor: 'pointer',
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={confirmarRetirada}
                            disabled={salvando}
                            style={{
                                padding: '10px 20px', borderRadius: '8px', border: 'none',
                                backgroundColor: salvando ? '#fd9393' : '#d81d1d', color: '#fff',
                                fontSize: '14px', fontWeight: '700', cursor: salvando ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', gap: '8px',
                            }}
                        >
                            {salvando ? 'Salvando...' : <><CheckCircle size={15} /> Confirmar Retirada</>}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ─── RENDER PRINCIPAL ────────────────────────────────────────────────────
    return (
        <div>
            <Modal />

            {/* Cabeçalho */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Controle de Experiência</h3>
                    <p style={{ margin: 0, fontSize: '14px', color: '#8b6464' }}>
                        Ex-funcionários recontratados no mesmo cargo e empresa — termo de experiência deve ser retirado no Populis.
                    </p>
                </div>
                <button
                    onClick={() => abaAtiva === 'pendentes' ? buscarPendentes() : buscarHistorico()}
                    disabled={loading}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '9px 16px', borderRadius: '8px', border: '1px solid #f0e2e2',
                        backgroundColor: '#fff', color: '#694747', fontSize: '14px',
                        fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer',
                    }}
                >
                    <RefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                    Atualizar
                </button>
            </div>

            {/* Banner de resultado do duplo-check após retirada */}
            {resultado && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '12px 16px', borderRadius: '10px', marginBottom: '16px',
                    backgroundColor: resultado.ajustePopulisConfirmado ? '#d1fae5' : '#fef9c3',
                    border: `1px solid ${resultado.ajustePopulisConfirmado ? '#6ee7b7' : '#fde047'}`,
                    color: resultado.ajustePopulisConfirmado ? '#065f46' : '#713f12',
                    fontSize: '14px', fontWeight: '600',
                }}>
                    {resultado.ajustePopulisConfirmado
                        ? <><ShieldCheck size={18} /> Retirado com sucesso e Populis confirmado.</>
                        : <><ShieldAlert size={18} /> Retirado com sucesso — Populis ainda não estava atualizado. Verifique após o próximo ciclo (04:30 · 12:30 · 16:20).</>
                    }
                    <button
                        onClick={() => setResultado(null)}
                        style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '16px', lineHeight: 1 }}
                        title="Fechar"
                    >✕</button>
                </div>
            )}

            {/* Abas */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', backgroundColor: '#f9f1f1', padding: '4px', borderRadius: '10px', width: 'fit-content' }}>
                {[
                    { id: 'pendentes', label: 'Pendentes', icon: ListChecks, badge: pendentes.length },
                    { id: 'historico', label: 'Histórico',  icon: History,    badge: null },
                ].map(({ id, label, icon: Icone, badge }) => (
                    <button
                        key={id}
                        onClick={() => setAbaAtiva(id)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '7px',
                            padding: '8px 18px', borderRadius: '7px', border: 'none',
                            fontWeight: '700', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s',
                            backgroundColor: abaAtiva === id ? '#fff' : 'transparent',
                            color: abaAtiva === id ? '#d81d1d' : '#8b6464',
                            boxShadow: abaAtiva === id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                        }}
                    >
                        <Icone size={15} />
                        {label}
                        {badge !== null && badge > 0 && (
                            <span style={{
                                backgroundColor: '#d81d1d', color: '#fff',
                                borderRadius: '999px', fontSize: '11px', fontWeight: '800',
                                padding: '1px 7px', minWidth: '20px', textAlign: 'center',
                            }}>
                                {badge}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Conteúdo */}
            {erro && (
                <div style={{ padding: '14px 18px', backgroundColor: '#fee2e2', borderRadius: '10px', color: '#dc2626', fontWeight: '600', marginBottom: '16px' }}>
                    {erro}
                </div>
            )}

            {loading ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#8b6464' }}>
                    <p style={{ fontSize: '15px' }}>Carregando...</p>
                </div>
            ) : (
                abaAtiva === 'pendentes' ? <TabelaPendentes /> : <TabelaHistorico />
            )}
        </div>
    );
};

export default RHControleExperiencia;
